import { LookupModelSearchEvent, LookupModelSelectionEvent, DataSourceExecutionEvent, DataSourceAction, Button, ClickEvent, Layout, SlideoutDecorator, ChangeEvent, Checkbox, DataSourceMode, ValidationResult, DataHeader } from "@mcleod/components";
import { StringUtil, ModelRow, McLeodClassicIntegration, Navigation } from "@mcleod/core";
import { AutogenLayoutGenericProfile } from "./autogen/AutogenLayoutGenericProfile";
import { EDITemplateSlideout, EDITemplateUpdateProps } from "./EDITemplateSlideout";
import { ModelEdiTemplate } from "./models/ModelEdiTemplate";
import { EdiCustomTransControl } from "./EdiCustomTransControl";
import { createCustomizationSlideout, handleButtonMasterBatchClick, handleMasterBatchCheck, isIntercompanyEnabled, setFuncAckTemplateValues, setTemplateValues } from "./EdiUtility";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { CommonDialogs } from "@mcleod/common";
import { ModelEdiProfile } from "./models/ModelEdiProfile";
import { ModelEdiremitProfile } from "./models/ModelEdiremitProfile";

const layoutURL = "lme/datafusion/GenericProfile";
export class GenericProfile extends AutogenLayoutGenericProfile {
    private FUNCTIONAL_ACK = "F";
    private APPLICATION_ADVICE = "P";
    private REMITTANCE_ADVICE = "A";
    private STATUS_INQUIRY = "Q";
    private TEXT_MESSAGE = "X";
    private OUTBOUND = "O";
    private INBOUND = "I";
    private XML = "X";
    private JSON = "J";
    private GENERIC = "G";
    private _isIcEnabled: boolean = false;

    override async onLoad() {
        this._isIcEnabled = await isIntercompanyEnabled();
        this.mainDataSource.doBeforeDataSourcePost = async (): Promise<boolean> => {
            // Check if we're in ADD mode and ask if user wants to add default events
            if (this.mainDataSource?.mode === DataSourceMode.ADD &&
                this.mainDataSource?.activeRow?.get("partner_id") &&
                this.mainDataSource?.activeRow?.get("alt_partner_id") &&
                this.mainDataSource?.activeRow?.get("version") &&
                this.mainDataSource?.activeRow?.get("company_id") &&
                this.mainDataSource?.activeRow?.get("direction")) {

                const validationResults: ValidationResult[] = this.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                    return false;
                }

                const partnerId = this.mainDataSource?.activeRow?.get("partner_id");
                const altPartnerId = this.mainDataSource?.activeRow?.get("alt_partner_id");
                const version = this.mainDataSource?.activeRow?.get("version");
                const companyId = this.mainDataSource?.activeRow?.get("company_id");
                const direction = this.mainDataSource?.activeRow?.get("direction");
                const transactionSet = this.mainDataSource?.activeRow?.get("transaction_set", "214");
                if (await this.profileExists(partnerId, altPartnerId, version, companyId, direction, transactionSet)) {
                    CommonDialogs.showError("Partner profile already exists");
                    return false;
                }
            }
            const templateId = this.mainDataSource?.activeRow?.get("template_id");
            if (!StringUtil.isEmptyString(templateId)) {
                const template = await new ModelEdiTemplate().searchSingle({ id: templateId });
                if (template.getString("transaction_type") !== this.mainDataSource?.activeRow?.get("transaction_type")) {
                    CommonDialogs.showError("The selected template's transaction type does not match the transaction type of this profile.");
                    return false;
                }
            }

            const validTransactionSets = ["997", "824", "820", "213", "864", "816"];
            if (!validTransactionSets.includes(this.mainDataSource?.activeRow?.get("transaction_set"))) {
                CommonDialogs.showError("Invalid transaction set. Generic Profiles are for 213s, 820s, 824s, 864s, 816's, or 997s.");
                return false;
            }
            if (this.invalidTransactionType(this.mainDataSource?.activeRow?.get("transaction_type"), this.mainDataSource?.activeRow?.get("transaction_set"))) {
                return false;
            }


            // Return true to allow the save to proceed
            return true;
        };
    }

    public static navigateTo(recordId: string) {
        const url = layoutURL + `?mode=update&key=${recordId}`;
        Navigation.navigateTo(url, { newTab: true })
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.edi.EntryEdiOrderProfile", "<ext> search id ='" + this.mainDataSource?.activeRow?.get("id") + "'");
    }

    templateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.direction = this.INBOUND;
        event.filter.transaction_type_input_generic = true;
    }

    funcAckCommunicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK);
    }

    funcAckTemplateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.templateBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK, this.OUTBOUND);
    }

    communicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.GENERIC);
    }

    async funcAckCommunicationsIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                setFuncAckTemplateValues(template, this.mainDataSource);
            }
        }
    }

    async templateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                this.setFuncAckTab(template.get("format_type") !== this.XML && template.get("format_type") !== this.JSON);
                setTemplateValues(template, this.mainDataSource);
            }
        }
    }

    async afterExecution(event: DataSourceExecutionEvent) {
        this.tabsetGenericProfile.visible = this.mainDataSource?.mode !== DataSourceMode.ADD;
        await this.loadTemplateData();
        this.updateUIVisibility();
        this.setNextBatchFields();
        if (event && event.getAction() === DataSourceAction.UPDATE) {
            this.buttonSetup.enabled = true;
        }
        this.setFuncAckTab(this.mainDataSource?.activeRow?.getString("edi_template.format_type") !== this.XML && this.mainDataSource?.activeRow?.getString("edi_template.format_type") !== this.JSON);
        this.setCompanyField(this.mainDataSource?.activeRow?.getBoolean("intercompany") && this._isIcEnabled);
        // Set visibility based on direction
        const direction = this.mainDataSource?.activeRow?.get("direction");
        const transactionType = this.mainDataSource?.activeRow?.get("transaction_type");
        this.buttonSetup.enabled = this.mainDataSource?.activeRow?.get("template_id") && this.mainDataSource.mode !== DataSourceMode.ADD;
        this.buttonAckTemplateSetup.enabled = this.mainDataSource?.activeRow?.get("func_ack_tid") ? true : false;
        this.tabFunctionalAcknowledgement.visible = transactionType !== this.FUNCTIONAL_ACK;
        this.dataHeader.title = 'Generic Profile';
    }

    /** This is an event handler for the onChange event of textboxFuncAckTid. */
    textboxFuncAckTidOnChange(event: ChangeEvent) {
        this.buttonAckTemplateSetup.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
    }

    beforeExecution(event: DataSourceExecutionEvent) {
        if (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND && !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("edi_customer_id"))) {
            this.mainDataSource?.activeRow?.set("edi_customer", {
                edi_customer_id: this.mainDataSource?.activeRow?.get("edi_customer_id"),
                edi_customer_name: this.mainDataSource?.activeRow?.get("edi_customer_name"),
                edi_customer_scac: this.mainDataSource?.activeRow?.get("edi_customer_scac"),
                edi_carrier: this.mainDataSource?.activeRow?.get("edi_carrier")
            });
        }
    }

    private async loadTemplateData() {
        await Promise.all([
            this.sourceEdiTemplate.search({ id: this.mainDataSource?.activeRow?.get("template_id") })
        ]);
    }

    private updateUIVisibility() {
        const direction = this.mainDataSource?.activeRow?.get("direction");
        const isInbound = direction === this.INBOUND;

        this.layoutNotifications.tableEdiNotifyInbound.visible = isInbound;
        this.layoutNotifications.tableEdiNotifyOutbound.visible = !isInbound;
        this.tabFunctionalAcknowledgement.visible = isInbound;
    }

    setupButtonOnClick() {
        this.setupOnClick("template_id", (returnedValues) => {
            this.updateFromTemplate(returnedValues);
        });
    }

    setupFuncAckButtonOnClick() {
        this.setupOnClick("func_ack_tid", (returnedValues) => {
            this.updateFuncAckFromTemplate(returnedValues);
        });
    }

    async doAfterModeChange() {
        this.setTitle();
    }

    doBeforeModeChange() {
        this.setTitle();
    }

    updateFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxTemplateId.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource?.activeRow?.get("template_id"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource?.activeRow?.setLookupModelData("template_id", lookupModelRow);
        this.textboxTemplateId.displayData(this.mainDataSource?.activeRow, null, 0)
        this.mainDataSource?.activeRow?.set("version", updatedValues.version);
        this.mainDataSource?.activeRow?.set("transaction_set", updatedValues.transactionSet);
        if (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND) {
            this.mainDataSource?.activeRow?.set("partner_id", updatedValues.appSenderId);
            this.mainDataSource?.activeRow?.set("alt_partner_id", updatedValues.senderId);
        } else {
            this.mainDataSource?.activeRow?.set("partner_id", updatedValues.appReceiverId);
            this.mainDataSource?.activeRow?.set("alt_partner_id", updatedValues.receiverId);
        }
    }
    updateFuncAckFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxFuncAckTid.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource?.activeRow?.get("func_ack_tid"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource?.activeRow?.setLookupModelData("func_ack_tid", lookupModelRow);
        this.textboxFuncAckTid.displayData(this.mainDataSource?.activeRow, null, 0)
        this.mainDataSource?.activeRow?.set("func_version", updatedValues.version);
        if (this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND) {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appSenderId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.senderId);
        } else {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appReceiverId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.receiverId);
        }
    }

    private commBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string) {
        event.filter.direction_input = this.mainDataSource?.activeRow?.get("direction");
        event.filter.type = type;
    }

    private templateBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string, direction: string) {
        event.filter.direction = direction;
        event.filter.transaction_type = type;
    }

    private setupOnClick(fieldName: string, callback: (returnedValues: EDITemplateUpdateProps) => void) {
        EDITemplateSlideout.showSlideout({
            templateId: this.mainDataSource?.activeRow?.get(fieldName), doAfterSlideOut:
                (returnedValues: EDITemplateUpdateProps) => {
                    callback(returnedValues);
                }
        });
    }

    private setNextBatchFields() {
        if (!this.checkboxFuncUseMBatch.checked) {
            this.textboxFuncNextBatch.visible = true;
            this.labelNextBatchNumber.visible = false;
            this.labelFuncMasterBatch.visible = false;
        } else {
            this.textboxFuncNextBatch.visible = false;
            this.labelNextBatchNumber.visible = true;
            this.labelFuncMasterBatch.visible = true;
        }
    }
    /** This is an event handler for the onClick event of buttonFilenameCustomize. */
    buttonFilenameCustomizeOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Functional Acknowledgement Filename",
            this.mainDataSource,
            "func_ack_filename",
            "fileMask"
        );
    }


    /** This is an event handler for the onClick event of buttonTransactionCustomize. */
    buttonTransactionCustomizeOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/datafusion/EdiCustomTransControl") as EdiCustomTransControl;

        const generateButton = new Button({
            caption: "Save & Close",
            rowBreak: false,
            color: "primary.reverse",
            backgroundColor: "primary",
            onClick: async (event: ClickEvent) => {
                const mask = layout.generateMask();
                if (layout.validateForbiddenCharacters()) {
                    this.mainDataSource.activeRow?.set("func_ack_st02_frmt", mask);
                    layout.slideOut();
                }
            }
        });
        const sod = new SlideoutDecorator({
            layout,
            title: "Customize Functional Acknowledgement Transmission Control Number",
            width: '45%',
            fillVerticalSpace: true,
            addlComponents: [generateButton],
            headerProps: {

            },
            doAfterSlideIn: async () => {
                layout.mask = this.mainDataSource?.activeRow?.get("func_ack_st02_frmt");
            }

        });
        sod.slideIn({ speed: 200 });
    }

    private setFuncAckTab(isVisible: boolean) {
        this.tabFunctionalAcknowledgement.visible = isVisible;
    }

    async masterBatchCheckboxOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const check = event.target as Checkbox;
            const partnerName = this.mainDataSource.activeRow.get("partner_name");
            const partnerID = this.mainDataSource.activeRow.get("partner_id");
            const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
            const version = this.mainDataSource.activeRow.get("version");

            await handleMasterBatchCheck(check, partnerName, partnerID, altPartnerId, version);
        }
    }

    /** This is an event handler for the onClick event of buttonMasterBatch. */
    async buttonMasterBatchOnClick(event: ClickEvent) {
        const partnerName = this.mainDataSource.activeRow.get("partner_name");
        const partnerID = this.mainDataSource.activeRow.get("func_partner_id");
        const altPartnerId = this.mainDataSource.activeRow.get("func_altpartner_id");
        const version = this.mainDataSource.activeRow.get("func_version");

        await handleButtonMasterBatchClick(partnerName, partnerID, altPartnerId, version, () => {
            this.checkboxFuncUseMBatch.checked = true;
        });
    }

    setTitle() {
        this.dataHeader.title = (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND ? "Inbound " : "Outbound ") + "Generic Profile";
    }

    async profileExists(profileId: string, altPartnerId: string, version: string, companyId: string, direction: string, transactionSet: string): Promise<boolean> {
        let profile = null;
        if (transactionSet === "820") {
            profile = await new ModelEdiremitProfile().searchSingle({
                partner_id: profileId,
                alt_partner_id: altPartnerId,
                version: version,
                company_id: companyId,
                direction: direction,
                transaction_set: transactionSet
            });
        } else {
            profile = await new ModelEdiProfile().searchSingle({
                partner_id: profileId,
                alt_partner_id: altPartnerId,
                version: version,
                company_id: companyId,
                direction: direction,
                transaction_set: transactionSet
            });
        }

        return profile !== null && profile !== undefined;
    }

    private setCompanyField(isEnabled: boolean) {
        this.textboxSendToCompany.visible = isEnabled;
        this.textboxCommId.visible = !isEnabled;
    }

    invalidTransactionType(transactionType: string, transactionSet: string): boolean {
        const transactionSetRules: { expectedType: string; sets: string[]; description: string }[] = [
            { expectedType: this.FUNCTIONAL_ACK, sets: ["997"], description: "997 - Functional Acknowledgement" },
            { expectedType: this.APPLICATION_ADVICE, sets: ["824"], description: "824 - Application Advice" },
            { expectedType: this.REMITTANCE_ADVICE, sets: ["820"], description: "820 - Remittance Advice" },
            { expectedType: this.STATUS_INQUIRY, sets: ["213"], description: "213 - Status Inquiry" },
            { expectedType: this.TEXT_MESSAGE, sets: ["864", "816"], description: "864 or 816 - Text Message" }
        ];

        const matchedRule = transactionSetRules.find(rule => rule.sets.includes(transactionSet) && transactionType !== rule.expectedType);
        if (matchedRule) {
            CommonDialogs.showError(`Invalid transaction type.\n${matchedRule.description}.`, "Invalid Transaction Type");
            return true;
        }
        return false;
    }
}
